import type {AutoValue, HideableValue, OmitStrict, PartiallyOptional, Values} from '@wix/devzai-utils-common';
import {
    assertNotNullable,
    assertType,
    autoValueAssert,
    autoValueCreate,
    autoValueGetValue,
    autoValueIsAutoValue,
    evaluateFunction,
    hideableValueCreateHidden,
    hideableValueGetValue,
    hideableValueIsHidden,
    hideableValueIsVisible,
    HtmlObjectHorizontalPosition,
    HtmlObjectVerticalPosition, assertHideableValue, hideableValueGetVisibleValue, hideableValueIsVisibleAndNotEqualValue } from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {assertWixMediaResource} from '@wix/devzai-common-wix';
import type {DoppeDtoAction} from '../types/doppe-dto-action';
import {assertDoppeDtoAction, doppeDtoActionExtendWithDefaultProps} from '../types/doppe-dto-action';
import type {DoppePageLockSettings} from '../types/doppe-page-lock-settings/doppe-page-lock-settings';
import {doppePageLockSettingsGetDefault} from '../types/doppe-page-lock-settings/doppe-page-lock-settings';
import {
    bymoPageGetInnerNavigatablePages,
    DoppeDtoInnerPage,
    doppeDtoInnerPageGetDefaultDesktopMainMediaSettings,
    doppeDtoInnerPageIsContent,
    DoppeDtoInnerPageKnownIds,
    doppeDtoInnerPageMigrateBlogPostPageFromDefaultValues,
    doppeDtoInnerPageMigrateBlogPostsPageFromDefaultValues,
    doppeDtoInnerPageMigrateEventPageFromDefaultValues,
    doppeDtoInnerPageMigrateEventsPageFromDefaultValues,
    doppeDtoInnerPageMigrateProductPageFromDefaultValues,
    doppeDtoInnerPageMigrateDigitalProductPageFromDefaultValues,
    doppeDtoInnerPageMigrateProductsPageFromDefaultValues,
    doppeDtoInnerPageMigrateServicesPageFromDefaultValues,
    doppeDtoInnerPageMigrateTicketingEventPageFromDefaultValues,
    doppeDtoInnerPageMigrateCoursePageFromDefaultValues,
    doppeDtoInnerPageMigrateAppointmentPageFromDefaultValues,
    doppeDtoInnerPageMigrateClassPageFromDefaultValues,
    doppeDtoInnerPageMigrateCartPageFromDefaultValues,
} from '../types/doppe-dto-inner-page';
import type {BymoActionsSettings} from './bymo-actions-settings';
import {
    assertBymoActionsSettings,
    BymoActionsMode,
    PrimaryActionViewType,
    SecondaryActionsViewType
} from './bymo-actions-settings';
import {DoppeActionButtonVariation} from '../types/doppe-action-button-variation';
import type {
    BymoTemplateMetadata,
    DoppeColorPalette,
    DoppeColorPaletteCalculatedColorsData,
    DoppeTemplateInstance
} from '../../doppe-sdk';
import {
    assertDoppeColorPalette,
    assertDoppeTemplateInstance,
    doppeColorPaletteCreate,
    doppePageFontEnsureSupportedFontSpec,
    doppePageFontGetDefaultFontFamily,
    doppePageFontResolveStyleSheetUrl
} from '../../doppe-sdk';
import React, {useContext} from 'react';
import {DoppeActionButtonTemplateIds} from '../doppe-templates/doppe-templates';
import {DoppeViewerLang} from '../doppe-viewer/doppe-viewer-lang';
import type {DoppeDtoSocialLink, DoppeDtoSocialLinkPlatform} from '../types/doppe-dto-social-link';
import {doppeDtoSocialLinkPlatformResolveUniqueName} from '../types/doppe-dto-social-link';
import type {DoppeDtoUser, LinkSocialShareSettingInPageProps, PageSEOSettingsProps} from '../types';
import {
    DoppeActionLayout,
    DoppeDtoBymoLinkSocialShareFieldsWithHideableValuesAssertions,
    DoppeDtoBymoPageSEOFieldsAssertions
} from '../types';
import type {BymoPagePropsMigrationData} from './bymo-page-props-migrations';
import type {BymoTemplateDesktopLayoutData} from './bymo-page-desktop-layout';
import {BymoTemplateDesktopLayout, BymoTemplateStandardDesktopLayoutContentWidth} from './bymo-page-desktop-layout';
import type {BymoPageMedia} from './bymo-page-media';
import type {BymoPageMediaMaskingShape} from './bymo-page-media-masking-shape/bymo-page-media-masking-shape';
import {BymoPageMediaMaskingStandardShape} from './bymo-page-media-masking-shape/bymo-page-media-masking-shape';
import type {BymoPagePropsContentSettingsMetadata} from '../bymo-page-props-metadata/bymo-page-props-metadata';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    BymoPagePropsSpecialContentSettingMetadata,
    bymoPagePropsCreateContentSettingMetadata
} from '../bymo-page-props-metadata/bymo-page-props-metadata';
import type {
    BymoPageNavigationMenuItem,
    BymoPageNavigationSettings,
    DoppeMenuTitleSource
} from './bymo-page-navigation-settings';
import {
    BymoPageNavigationMenuDesktopMenuType,
    bymoPageNavigationMenuItemMigrateFromDefaultValues,
    BymoPageNavigationMenuItemType,
    BymoPageNavigationMenuSettingsMenuPanelDisplayType,
    DoppeMenuLogoProfilePictureAutoValue,
    doppeMenuLogoSourceResolve,
    DoppeMenuTitleAutoSource,
    doppeMenuTitleSourceResolve,
    DoppeViewerNavigationMenuStyle
} from './bymo-page-navigation-settings';

import {
    BymoTemplateSpeedDialMenuAppearance,
    BymoTemplateSpeedDialSearchAppearance,
    BymoTemplateSpeedDialSocialLinksAppearance
} from '../../components/bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial-common';
import {BymoPageTextSize} from './bymo-page-text-size';

export const BymoTemplateLayout = {
    Standard: 'Standard',
    Blank: 'Blank',
    Layout1: 'Layout1',
    Layout2: 'Layout2',
    Layout3: 'Layout3',
    Layout4: 'Layout4',
    Layout5: 'Layout5',
    Layout6: 'Layout6',
    Layout7: 'Layout7',
    Layout8: 'Layout8',
    Layout9: 'Layout9',
    Layout10: 'Layout10',
    Layout11: 'Layout11',
    Layout12: 'Layout12',
    Layout13: 'Layout13',
    Layout14: 'Layout14',
} as const;

export type BymoTemplateLayout = Values<typeof BymoTemplateLayout>;

export interface BymoTemplateLayoutData {
    layout: BymoTemplateLayout;
}


export const PreRollExperience = {
    /**
     * The user will get a pre-roll experience of our choice.
     */
    Auto: 'Auto',
    StickyHeader: 'StickyHeader',
    FloatingHeader: 'FloatingHeader',
} as const;

export type PreRollExperience = Values<typeof PreRollExperience>;

export type BymoPageSocialLink = DoppeDtoSocialLink & {
    hidden?: boolean;
};

export function bymoPageSocialLinkIsHidden(bymoPageSocialLink: BymoPageSocialLink) {
    return bymoPageSocialLink.hidden === true;
}

export interface BymoPageSocialLinksProps {
    socialLinks: BymoPageSocialLink[];
}

export interface BymoPageHasSearchProps {
    searchAppearance: BymoTemplateSpeedDialSearchAppearance;
    searchPlaceholder: HideableValue<string>;
}

export interface BymoPageSiteSearchSettings {
    showActionsInSearch: boolean;
    showLinksInSearch: boolean;
    showPagesInSearch: boolean;
    showProductsInSearch: boolean;
    showServicesInSearch: boolean;
    showEventsInSearch: boolean;
    showBlogPostsInSearch: boolean;
}

export interface BymoPageFeatureHasActions {
    actions: DoppeDtoAction[];
    actionsSettings: BymoActionsSettings;
}

export interface BymoPageFeatureHasInnerPages {
    innerPages: DoppeDtoInnerPage.Content[];
    defaultStoreProductsPage: DoppeDtoInnerPage.Products,
    defaultBlogPostsPage: DoppeDtoInnerPage.BlogPosts,
    defaultEventsPage: DoppeDtoInnerPage.Events,
    defaultEventPage: DoppeDtoInnerPage.Event,
    defaultTicketingEventPage: DoppeDtoInnerPage.TicketingEvent,
    defaultProductPage: DoppeDtoInnerPage.Product,
    defaultDigitalProductPage: DoppeDtoInnerPage.DigitalProduct,
    defaultServicesPage: DoppeDtoInnerPage.Services,
    defaultCoursePage: DoppeDtoInnerPage.Course,
    defaultAppointmentPage: DoppeDtoInnerPage.Appointment,
    defaultClassPage: DoppeDtoInnerPage.Class,
    defaultBlogPostPage: DoppeDtoInnerPage.BlogPost,
    defaultCartPage: DoppeDtoInnerPage.Cart,
    navigationSettings: BymoPageNavigationSettings;
    menuAppearance: BymoTemplateSpeedDialMenuAppearance;
}

export interface BymoPageFeatureHasActionButton {
    actionButtonTemplate: DoppeTemplateInstance;
}


export interface BymoPageFeatureCanBeSkipped {
    skipText: string;
    autoSkipEnabled: boolean;
    autoSkipSeconds: number;
    autoSkipShowCancel: boolean;
    preRollExperience: PreRollExperience;
}

export const DoppePageMediaAutoSource = {
    AccountProfilePicture: 'AccountProfilePicture'
} as const;

export type DoppePageMediaAutoSource = Values<typeof DoppePageMediaAutoSource>;

export const DoppeProfileNameAutoSource = {
    AccountName: 'AccountName',
    DisplayName: 'DisplayName',
} as const;

export type DoppeProfileNameAutoSource = Values<typeof DoppeProfileNameAutoSource>;


export type DoppePageProfilePictureSource = WixMediaResource | null | AutoValue<DoppePageMediaAutoSource>;
export type DoppePageProfileNameSource = null | AutoValue<DoppeProfileNameAutoSource> | string;

export function doppePageMediaAutoSourceResolve<T extends BymoPageMedia>(
    mediaAutoSource: T | null | AutoValue<DoppePageMediaAutoSource>,
    context: {
        accountProfilePicture: DoppeDtoUser['profilePicture'];
    }
) {
    if (autoValueIsAutoValue<DoppePageMediaAutoSource>(mediaAutoSource)) {
        const autoSource = autoValueGetValue(mediaAutoSource);

        switch (autoSource) {
            case DoppePageMediaAutoSource.AccountProfilePicture: {
                return context.accountProfilePicture;
            }
        }
    } else {
        return mediaAutoSource;
    }
}

export interface BymoPageFeatureHasProfileContent {
    profilePicture: HideableValue<DoppePageProfilePictureSource>;
    /**
     * Note: This property is a hack that's solves the issue of having link in bio users with
     * profilePicture set to some value, when actually we initially wanted their profile picture to
     * be bound to their account profile picture.
     */
    profilePictureLinkInBio: HideableValue<DoppePageProfilePictureSource>;
    profilePictureMaskingShape: BymoPageMediaMaskingShape;
    profileName: HideableValue<DoppePageProfileNameSource>;
    showVerifiedCheck: boolean;
}

export const BymoPageBackgroundImageLayout = {
    Fill: 'fill',
    Tile: 'tile'
} as const;

export type BymoPageBackgroundImageLayout = Values<typeof BymoPageBackgroundImageLayout>;

export type BymoPageBackgroundMediaSettings = {
    mediaInfo: WixMediaResource;
    opacity?: number;
    horizontalPosition?: HtmlObjectHorizontalPosition;
    verticalPosition?: HtmlObjectVerticalPosition;
    layout?: BymoPageBackgroundImageLayout;
    tileLayoutSize?: number;
    tileHorizontalPosition?: HtmlObjectHorizontalPosition;
}

export const BymoPageBackgroundMediaSettingsDefaultValues = {
    layout: BymoPageBackgroundImageLayout.Fill,
    horizontalPosition: HtmlObjectHorizontalPosition.Center,
    verticalPosition: HtmlObjectVerticalPosition.Center,
    tileLayoutSize: 33,
    tileHorizontalPosition: HtmlObjectHorizontalPosition.Left
}

export interface BymoPageFeatureHasVisibleBackground {
    backgroundImage: BymoPageBackgroundMediaSettings | null;
    desktopBackgroundImage: BymoPageBackgroundMediaSettings | null;
    /**
     * Desktop-specific content background settings.
     * When visible, desktop uses this background for the content area.
     * When hidden, desktop uses the same background as mobile (backgroundImage).
     */
    desktopContentBackground: HideableValue<BymoPageBackgroundMediaSettings | null>;
}

export interface BymoPageFeatureHasCustomTextualContent {
    callToActionText: HideableValue<string>;
    headingText: HideableValue<string>;
}

export type BymoPageFeaturesProps = {
    hasActions: BymoPageFeatureHasActions;
    canBeSkipped: BymoPageFeatureCanBeSkipped;
    hasProfileContent: BymoPageFeatureHasProfileContent;
    hasVisibleBackground: BymoPageFeatureHasVisibleBackground;
    hasCustomTextualContent: BymoPageFeatureHasCustomTextualContent;
    hasActionButton: BymoPageFeatureHasActionButton;
}

export type BymoPageFeatures = keyof BymoPageFeaturesProps;

export const BymoPageAllSupportedFeatures: BymoPageFeatures[] = [
    'hasActions',
    'hasActionButton',
    'canBeSkipped',
    'hasProfileContent',
    'hasVisibleBackground',
    'hasCustomTextualContent',
]


export interface BymoPageSharedCommonProps extends BymoPageFeatureHasInnerPages {
    colorPalette: DoppeColorPalette;
    textFontFamily: string;
    profileNameCustomTextFontFamily: HideableValue<string>;
    pageLayout: BymoTemplateLayoutData;
    desktopLayout: BymoTemplateDesktopLayoutData;
    pageTitle: HideableValue<string>;
    socialShareSettings: LinkSocialShareSettingInPageProps | null;
    seoSettings: PageSEOSettingsProps | null;
    showCookieBanner: boolean;
    showPrivacyAndTerms: boolean;
    enableGoogleAnalytics: boolean;
    lang: DoppeViewerLang;
    removeHoppBanner: boolean;
    removeHoppWatermark: boolean;
    heroPhotos: WixMediaResource[] | null;
    heroPhotosOpacity: number;
    coverPhoto: HideableValue<WixMediaResource | null>;
    standardDesktopLayoutContentWidth: BymoTemplateStandardDesktopLayoutContentWidth;
    textSize: BymoPageTextSize;
    appliedMigrations: BymoPagePropsMigrationData[];
    devMetadata: {
        socialImages: WixMediaResource[];
        followersCount: string;
        followingCount: string;
        postsCount: string;
        bioText: string;
        userName: string;
    };
    devCustomPageContentWidthInWideLayout: HideableValue<string>;
    /**
     * When this flag is enabled, all the font sizes will be set to zero, so that we can make sure that there are no hardcoded font sizes in the css.
     */
    devApplyZeroValueTextSize: boolean;
}

/**
 * Represents the values of the properties that are common to all the templates.
 */
export interface BymoPageCommonProps extends BymoPageSharedCommonProps,
    BymoPageSiteSearchSettings,
    BymoPageFeatureHasActions,
    BymoPageFeatureCanBeSkipped,
    BymoPageFeatureHasProfileContent,
    BymoPageFeatureHasVisibleBackground,
    BymoPageFeatureHasCustomTextualContent,
    BymoPageFeatureHasActionButton
{
    latestNonPremiumValues: LatestNonPremiumValues;
    lockSettings: DoppePageLockSettings;
    showOnlyCurrentPageContentInSearch: boolean;
}

export type LatestNonPremiumValues = Partial<Pick<BymoPageCommonProps,
    'textFontFamily' | 'actionButtonTemplate' | 'pageLayout' | 'desktopLayout'>>;

/**
 * Represents the values of the properties that are common to all the templates and that can be stored in the db for a bymo page.
 * All the values are optional, since along time more properties will be added, and previously saved user pages
 * won't contain the newly added property values.
 */
export type PartialBymoPageCommonProps = OmitStrict<Partial<BymoPageCommonProps>, 'actionsSettings' | 'actions' | 'devMetadata'> & {
    actionsSettings?: Partial<BymoActionsSettings>;
    actions?: PartiallyOptional<DoppeDtoAction, 'icon' | 'widgetOpeningMode'>[];
    devMetadata?: Partial<BymoPageCommonProps['devMetadata']>;
}

/**
 * Represents the properties' values that can be stored for a bymo page.
 */
export type PartialBymoPageProps<TEMPLATE_PROPS extends {} = Record<string, any>> =
    Partial<TEMPLATE_PROPS>
    & PartialBymoPageCommonProps;

/**
 * Represents the properties' values that can be stored for a bymo page.
 */
export type BymoPagePropsWithPartialTemplateProps<TEMPLATE_PROPS extends {} = Record<string, any>> =
    Partial<TEMPLATE_PROPS>
    & BymoPageCommonProps;

export type BymoPageProps<TEMPLATE_PROPS extends {} = Record<string, any>> = TEMPLATE_PROPS & BymoPageCommonProps

export type BymoPagePropsForViewer<TEMPLATE_PROPS extends {} = Record<string, any>> = BymoPageProps<TEMPLATE_PROPS> & {
    __forViewer: true,
    colorPaletteCalculatedColorsData: DoppeColorPaletteCalculatedColorsData
}

export function bymoPagePropsIsProcessedForViewer<TEMPLATE_PROPS extends {} = Record<string, any>> (
    value: PartialBymoPageProps<TEMPLATE_PROPS> | BymoPagePropsForViewer<TEMPLATE_PROPS>
) : value is BymoPagePropsForViewer<TEMPLATE_PROPS> {
    return '__forViewer' in value;
}

export function bymoPagePropsResolveFromPartialProps<TEMPLATE_PROPS extends {} = {}>(
    bymoPageCommonProps: PartialBymoPageProps<TEMPLATE_PROPS>
): BymoPageProps<Partial<TEMPLATE_PROPS>>
export function bymoPagePropsResolveFromPartialProps<TEMPLATE_PROPS extends {} = {}>(
    bymoPageCommonProps: PartialBymoPageProps<TEMPLATE_PROPS>,
    bymoTemplateMetadata: BymoTemplateMetadata<TEMPLATE_PROPS>
): BymoPageProps<TEMPLATE_PROPS>
export function bymoPagePropsResolveFromPartialProps<TEMPLATE_PROPS extends {} = {}>(
    bymoPageCommonProps: PartialBymoPageProps<TEMPLATE_PROPS>,
    bymoTemplateMetadata?: BymoTemplateMetadata<TEMPLATE_PROPS>
): BymoPageProps<Partial<TEMPLATE_PROPS>> | BymoPageProps<TEMPLATE_PROPS> {

    const currentProps = bymoPageCommonPropsResolveFromPartial(bymoPageCommonProps);

    // const commonPropsResolvedFromPartialPropsAndTemplateInitalValues = {
    //     ...evaluateWhenFunction(bymoTemplateMetadata?.initialValues, currentProps),
    //     ...currentProps
    // } as BymoPageProps<Partial<TEMPLATE_PROPS>> | BymoPageProps<TEMPLATE_PROPS>;
    return (
        bymoTemplateMetadata?.resolveProps ?
        bymoTemplateMetadata.resolveProps(currentProps as BymoPagePropsWithPartialTemplateProps<TEMPLATE_PROPS>) :
        {
            ...bymoTemplateMetadata?.initialValues,
            ...currentProps
        } as BymoPageProps<TEMPLATE_PROPS>
    );

}

function bymoPageCommonPropsResolveFromPartial(bymoPageCommonProps: PartialBymoPageCommonProps): BymoPageCommonProps {

    const {
        actionsSettings,
        actions = [],
        lang = DoppeViewerLang.English,
        devMetadata,
        innerPages = [],
        navigationSettings = bymoPageCreateDefaultNavigationSettings(),
        defaultStoreProductsPage,
        defaultBlogPostsPage,
        defaultBlogPostPage,
        defaultEventsPage,
        defaultEventPage,
        defaultTicketingEventPage,
        defaultServicesPage,
        defaultCoursePage,
        defaultAppointmentPage,
        defaultClassPage,
        defaultProductPage,
        defaultDigitalProductPage,
        defaultCartPage,
        ...restProps
    } = bymoPageCommonProps;

    return {
        appliedMigrations: [],
        innerPages: innerPages.map((innerPage) => {
            if (!doppeDtoInnerPageIsContent(innerPage)) {
                return innerPage;
            }

            const {
                actions,
                socialShareSettings = null,
                seoSettings = null,
                desktopMainMediaSettings,
                backgroundImage,
                desktopContentBackground,
                desktopBackgroundImage,
                ...innerPageRestProps
            } = innerPage;

            return {
                ...innerPageRestProps,
                lockSettings: innerPageRestProps.lockSettings ?? doppePageLockSettingsGetDefault(),
                socialShareSettings: socialShareSettings,
                seoSettings: seoSettings,
                actions: actions ? actions.map(action => {
                    return doppeDtoActionExtendWithDefaultProps(action as DoppeDtoAction<any>);
                }) : [],
                desktopMainMediaSettings: desktopMainMediaSettings ?? doppeDtoInnerPageGetDefaultDesktopMainMediaSettings(),
                backgroundImage: backgroundImage ?? hideableValueCreateHidden(null),
                desktopContentBackground: desktopContentBackground ?? hideableValueCreateHidden(null),
                desktopBackgroundImage: desktopBackgroundImage ?? hideableValueCreateHidden(null),
            }
        }),
        actions: actions.map(action => {
            return doppeDtoActionExtendWithDefaultProps(action as DoppeDtoAction<any>);
        }),
        menuAppearance: BymoTemplateSpeedDialMenuAppearance.Default,
        navigationSettings: bymoPageMigrateNavigationSettingsFromDefaultValues(navigationSettings),
        defaultStoreProductsPage: doppeDtoInnerPageMigrateProductsPageFromDefaultValues(defaultStoreProductsPage),
        defaultBlogPostsPage: doppeDtoInnerPageMigrateBlogPostsPageFromDefaultValues(defaultBlogPostsPage),
        defaultEventsPage: doppeDtoInnerPageMigrateEventsPageFromDefaultValues(defaultEventsPage),
        defaultServicesPage: doppeDtoInnerPageMigrateServicesPageFromDefaultValues(defaultServicesPage),
        defaultCoursePage: doppeDtoInnerPageMigrateCoursePageFromDefaultValues(defaultCoursePage),
        defaultAppointmentPage: doppeDtoInnerPageMigrateAppointmentPageFromDefaultValues(defaultAppointmentPage),
        defaultClassPage: doppeDtoInnerPageMigrateClassPageFromDefaultValues(defaultClassPage),
        defaultProductPage: doppeDtoInnerPageMigrateProductPageFromDefaultValues(defaultProductPage),
        defaultDigitalProductPage: doppeDtoInnerPageMigrateDigitalProductPageFromDefaultValues(defaultDigitalProductPage),
        defaultEventPage: doppeDtoInnerPageMigrateEventPageFromDefaultValues(defaultEventPage),
        defaultTicketingEventPage: doppeDtoInnerPageMigrateTicketingEventPageFromDefaultValues(defaultTicketingEventPage),
        defaultBlogPostPage: doppeDtoInnerPageMigrateBlogPostPageFromDefaultValues(defaultBlogPostPage),
        defaultCartPage: doppeDtoInnerPageMigrateCartPageFromDefaultValues(defaultCartPage),
        removeHoppBanner: false,
        removeHoppWatermark: false,
        lang: lang,
        textFontFamily: doppePageFontGetDefaultFontFamily(lang),
        profileNameCustomTextFontFamily: hideableValueCreateHidden(doppePageFontGetDefaultFontFamily(lang)),
        colorPalette: doppeColorPaletteCreate(['#F0F0F0', '#899BB5', '#2F3D59', '#363636']),
        standardDesktopLayoutContentWidth: BymoTemplateStandardDesktopLayoutContentWidth.Tiny,
        actionsSettings: {
            primaryActionId: undefined,
            primaryActionViewSettings: {
                viewType: PrimaryActionViewType.ActionButton,
                buttonVariation: DoppeActionButtonVariation.Primary,
                showSeparator: false,
                showActionHeader: true,
            },
            secondaryActionsViewSettings: {
                viewType: SecondaryActionsViewType.OnPage,
                buttonVariation: DoppeActionButtonVariation.Secondary
            },
            actionsMode: BymoActionsMode.MultipleAction,
            ...actionsSettings
        },
        profilePictureLinkInBio: autoValueCreate(DoppePageMediaAutoSource.AccountProfilePicture),
        profilePicture: null,
        profilePictureMaskingShape: BymoPageMediaMaskingStandardShape.Circle,
        coverPhoto: null,
        headingText: hideableValueCreateHidden('Title'),
        skipText: 'Skip',
        autoSkipSeconds: 15,
        autoSkipEnabled: false,
        autoSkipShowCancel: true,
        preRollExperience: PreRollExperience.Auto,
        callToActionText: hideableValueCreateHidden(''),
        backgroundImage: null,
        desktopBackgroundImage: null,
        desktopContentBackground: hideableValueCreateHidden(null),
        actionButtonTemplate: {
            templateId: DoppeActionButtonTemplateIds.Standard,
            templateProps: {}
        },
        profileName: autoValueCreate(DoppeProfileNameAutoSource.DisplayName),
        showVerifiedCheck: false,
        pageLayout: {
            layout: BymoTemplateLayout.Blank,
        },
        desktopLayout: {
            layout: BymoTemplateDesktopLayout.Standard,
        },
        seoSettings: null,
        showCookieBanner: true,
        showPrivacyAndTerms: true,
        enableGoogleAnalytics: true,
        socialShareSettings: null,
        pageTitle: hideableValueCreateHidden('Title'),
        heroPhotos: null,
        heroPhotosOpacity: 20,
        latestNonPremiumValues: {},
        textSize: BymoPageTextSize.Small,
        devMetadata: {
            socialImages:  [],
            followersCount: '783',
            followingCount: '67',
            postsCount: '243',
            bioText: '',
            userName: '',
            ...devMetadata
        },
        devCustomPageContentWidthInWideLayout: hideableValueCreateHidden('500px'),
        devApplyZeroValueTextSize: false,
        lockSettings: doppePageLockSettingsGetDefault(),
        showActionsInSearch: true,
        showLinksInSearch: true,
        showPagesInSearch: true,
        showProductsInSearch: true,
        showServicesInSearch: true,
        showEventsInSearch: true,
        showBlogPostsInSearch: true,
        showOnlyCurrentPageContentInSearch: false,
        ...restProps
    };
}

export function assertPartialBymoPageProps(bymoPageProps: PartialBymoPageProps): asserts bymoPageProps is PartialBymoPageProps;
export function assertPartialBymoPageProps(bymoPageProps: unknown): asserts bymoPageProps is PartialBymoPageProps;
export function assertPartialBymoPageProps(bymoPageProps: unknown): asserts bymoPageProps is PartialBymoPageProps {
    assertType<PartialBymoPageProps<{}>>(bymoPageProps, assert => {
        assert.isObject({
            appliedMigrations: assert => assert.optional.isArray(assert => assert.isObject({
                migration: assert => assert.isString(),
                metadata: assert => assert.isObject({}, {assertNoUnknownKeys: false})
            })),
            lang: assert => assert.optional.isString(),
            removeHoppBanner: assert => assert.optional.isBoolean(),
            removeHoppWatermark: assert => assert.optional.isBoolean(),
            headingText: assert => assert.optional.usingAssertionFunction(value => {
                assertHideableValue<string>(value, assert => assert.isString())
            }),
            callToActionText: assert => assert.optional.usingAssertionFunction(value => {
                assertHideableValue<string>(value, assert => assert.isString())
            }),
            profileName: assert => assert.optional.usingAssertionFunction(value => {
                assertHideableValue<DoppePageProfileNameSource>(value, assert => {
                    assert.isUnion(
                        assert => assert.isNull(),
                        assert => assert.isString(),
                        assert => assert.usingAssertionFunction(value => autoValueAssert<string>(value, assert => assert.isString()))
                    )
                })
            }),
            socialShareSettings: assert => assert.optional.isUnion(
                assert => assert.isNull(),
                assert => assert.isObject(DoppeDtoBymoLinkSocialShareFieldsWithHideableValuesAssertions)
            ),
            seoSettings: assert => assert.optional.isUnion(
                assert => assert.isNull(),
                assert => assert.isObject(DoppeDtoBymoPageSEOFieldsAssertions)
            ),
            showCookieBanner: assert => assert.optional.isBoolean(),
            showPrivacyAndTerms: assert => assert.optional.isBoolean(),
            enableGoogleAnalytics: assert => assert.optional.isBoolean(),
            devMetadata: assert => assert.optional.isAny(),
            showVerifiedCheck: assert => assert.optional.isBoolean(),
            preRollExperience: assert => assert.optional.isString(),
            skipText: assert => assert.optional.isString(),
            autoSkipEnabled: assert => assert.optional.isBoolean(),
            autoSkipShowCancel: assert => assert.optional.isBoolean(),
            autoSkipSeconds: assert => assert.optional.isNumber(),
            profilePicture: assert => assert.optional.usingAssertionFunction(value => {
                assertHideableValue<DoppePageProfilePictureSource>(value, assert => {
                    assert.isUnion(
                        assert => assert.isNull(),
                        assert => assert.usingAssertionFunction(assertWixMediaResource),
                        assert => assert.usingAssertionFunction(value => autoValueAssert<string>(value, assert => assert.isString()))
                    )
                })
            }),
            profilePictureLinkInBio: assert => assert.optional.usingAssertionFunction(value => {
                assertHideableValue<DoppePageProfilePictureSource>(value, assert => {
                    assert.isUnion(
                        assert => assert.isNull(),
                        assert => assert.usingAssertionFunction(assertWixMediaResource),
                        assert => assert.usingAssertionFunction(value => autoValueAssert<string>(value, assert => assert.isString()))
                    )
                })
            }),
            profilePictureMaskingShape: assert => assert.optional.isString(),
            coverPhoto: assert => assert.optional.usingAssertionFunction(value => {
                assertHideableValue<WixMediaResource | null>(value, assert => {
                    assert.isUnion(
                        assert => assert.isNull(),
                        assert => assert.usingAssertionFunction(assertWixMediaResource)
                    )
                })
            }),
            actions: assert => assert.optional.isArray(assert => {
                assert.usingAssertionFunction(assertDoppeDtoAction)
            }),
            colorPalette: assert => assert.optional.usingAssertionFunction(assertDoppeColorPalette),
            textFontFamily: assert => assert.optional.isString(),
            profileNameCustomTextFontFamily: assert => assert.optional.usingAssertionFunction(value => {
                assertHideableValue<string>(value, assert => assert.isString())
            }),
            actionsSettings: assert => assert.optional.usingAssertionFunction(assertBymoActionsSettings),
            backgroundImage: assert => assert.optional.isUnion(
                assert => assert.isNull(),
                assert => assert.isObject({
                    mediaInfo: assert => assert.usingAssertionFunction(assertWixMediaResource),
                    opacity: assert => assert.optional.isNumber(),
                    horizontalPosition: assert => assert.optional.isString(),
                    verticalPosition: assert => assert.optional.isString(),
                    layout: assert => assert.optional.isString(),
                    tileLayoutSize: assert => assert.optional.isNumber(),
                    tileHorizontalPosition: assert => assert.optional.isString(),
                })
            ),
            desktopBackgroundImage: assert => assert.optional.isUnion(
                assert => assert.isNull(),
                assert => assert.isObject({
                    mediaInfo: assert => assert.usingAssertionFunction(assertWixMediaResource),
                    opacity: assert => assert.optional.isNumber(),
                    horizontalPosition: assert => assert.optional.isString(),
                    verticalPosition: assert => assert.optional.isString(),
                    layout: assert => assert.optional.isString(),
                    tileLayoutSize: assert => assert.optional.isNumber(),
                    tileHorizontalPosition: assert => assert.optional.isString(),
                })
            ),
            desktopContentBackground: assert => assert.optional.usingAssertionFunction(value => {
                assertHideableValue<BymoPageBackgroundMediaSettings | null>(value, assert => {
                    assert.isUnion(
                        assert => assert.isNull(),
                        assert => assert.isObject({
                            mediaInfo: assert => assert.usingAssertionFunction(assertWixMediaResource),
                            opacity: assert => assert.optional.isNumber(),
                            horizontalPosition: assert => assert.optional.isString(),
                            verticalPosition: assert => assert.optional.isString(),
                            layout: assert => assert.optional.isString(),
                            tileLayoutSize: assert => assert.optional.isNumber(),
                            tileHorizontalPosition: assert => assert.optional.isString(),
                        })
                    )
                })
            }),
            actionButtonTemplate: assert => assert.optional.usingAssertionFunction(assertDoppeTemplateInstance),
            pageLayout: assert => assert.optional.isObject({
                layout: assert => assert.isString(),
            }, {
                assertNoUnknownKeys: false
            }),
            desktopLayout: assert => assert.optional.isObject({
                layout: assert => assert.isString(),
            }, {
                assertNoUnknownKeys: false
            }),
            devCustomPageContentWidthInWideLayout: assert => assert.optional.usingAssertionFunction(value => {
                assertHideableValue<string>(value, assert => assert.isString())
            }),
            pageTitle: assert => assert.optional.usingAssertionFunction(value => {
                assertHideableValue<string>(value, assert => assert.isString())
            }),
            heroPhotos: assert => assert.optional.isUnion(
                assert => assert.isNull(),
                assert => assert.isArray(assert => {
                    assert.usingAssertionFunction(assertWixMediaResource);
                })
            ),
            heroPhotosOpacity: assert => assert.optional.isNumber(),
            latestNonPremiumValues: assert => assert.optional.isAny(),
            innerPages: assert => assert.optional.isArray(assert => assert.isAny()),
            defaultStoreProductsPage: assert => assert.optional.isAny(),
            defaultBlogPostsPage: assert => assert.optional.isAny(),
            defaultEventsPage: assert => assert.optional.isAny(),
            defaultProductPage: assert => assert.optional.isAny(),
            defaultDigitalProductPage: assert => assert.optional.isAny(),
            defaultServicesPage: assert => assert.optional.isAny(),
            defaultCoursePage: assert => assert.optional.isAny(),
            defaultAppointmentPage: assert => assert.optional.isAny(),
            defaultClassPage: assert => assert.optional.isAny(),
            defaultEventPage: assert => assert.optional.isAny(),
            defaultTicketingEventPage: assert => assert.optional.isAny(),
            defaultBlogPostPage: assert => assert.optional.isAny(),
            defaultCartPage: assert => assert.optional.isAny(),
            navigationSettings: assert => assert.optional.isAny(),
            menuAppearance: assert => assert.optional.isString(),
            standardDesktopLayoutContentWidth: assert => assert.optional.isString(),
            textSize: assert => assert.optional.isString(),
            devApplyZeroValueTextSize: assert => assert.optional.isBoolean(),
            lockSettings: assert => assert.optional.isAny(),
            showActionsInSearch: assert => assert.optional.isBoolean(),
            showLinksInSearch: assert => assert.optional.isBoolean(),
            showPagesInSearch: assert => assert.optional.isBoolean(),
            showProductsInSearch: assert => assert.optional.isBoolean(),
            showServicesInSearch: assert => assert.optional.isBoolean(),
            showEventsInSearch: assert => assert.optional.isBoolean(),
            showBlogPostsInSearch: assert => assert.optional.isBoolean(),
            showOnlyCurrentPageContentInSearch: assert => assert.optional.isBoolean(),
        }, {
            assertNoUnknownKeys: false
        })
    })
}

export const BymoPagePropsCommonPropsMetadata = {
    headingText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.PageHeading),
    profileName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.ProfileNameSource, BymoPagePropsContentSettingSemanticMeaning.ProfileIdentity),
    pageTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.PageTitle),
    skipText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    callToActionText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    heroPhotos: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.PageHeroMedia),
    coverPhoto: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSemanticMeaning.PageCoverMedia),
    showActionsInSearch: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    showLinksInSearch: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    showPagesInSearch: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    showServicesInSearch: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    showProductsInSearch: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    showBlogPostsInSearch: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    showEventsInSearch: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    showOnlyCurrentPageContentInSearch: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    showVerifiedCheck: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    enableGoogleAnalytics: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    showPrivacyAndTerms: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    showCookieBanner: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    autoSkipSeconds: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    heroPhotosOpacity: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    autoSkipEnabled: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    autoSkipShowCancel: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    removeHoppBanner: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    removeHoppWatermark: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    devApplyZeroValueTextSize: BymoPagePropsSpecialContentSettingMetadata.Ignored,
} satisfies BymoPagePropsContentSettingsMetadata<OmitStrict<BymoPageCommonProps, 'actions' | 'textFontFamily' | 'profileNameCustomTextFontFamily' | 'devCustomPageContentWidthInWideLayout' | 'defaultEventPage' | 'defaultTicketingEventPage' | 'defaultBlogPostPage' | 'defaultDigitalProductPage'>>

export const BymoPagePropsContext = React.createContext<BymoPagePropsForViewer<{}> | null>(null);

export function useBymoPageProps() {
    return assertNotNullable(useContext(BymoPagePropsContext));
}

export function bymoPagePropsResolveTextFontFamilyStyleSheetUrl(bymoPageProps: BymoPageProps) {
    const textFontFamilySpec = doppePageFontEnsureSupportedFontSpec(bymoPageProps.textFontFamily, bymoPageProps.lang);

    return doppePageFontResolveStyleSheetUrl({
        fontFamily: textFontFamilySpec.fontFamily,
        fontWeights: [textFontFamilySpec.headingsFontWeight, textFontFamilySpec.textFontWeight]
    })
}

export function bymoPagePropsResolveProfileNameFontStyleSheetUrl(bymoPageProps: BymoPageProps): string | null {
    const customFontFamily = hideableValueGetVisibleValue(bymoPageProps.profileNameCustomTextFontFamily, null);

    if (customFontFamily === null) {
        return null;
    }

    const fontSpec = doppePageFontEnsureSupportedFontSpec(customFontFamily, bymoPageProps.lang);

    return doppePageFontResolveStyleSheetUrl({
        fontFamily: fontSpec.fontFamily,
        fontWeights: [fontSpec.headingsFontWeight, fontSpec.textFontWeight]
    }) ?? null;
}

/**
 * Finds a specific action by ID across all locations (main page, inner pages, locked actions).
 * More efficient than resolving all actions and then filtering.
 * Returns null if the action is not found.
 */
export function bymoPagePropsFindActionById(
    bymoPageProps: BymoPageProps | PartialBymoPageProps,
    actionId: DoppeDtoAction.Id
): DoppeDtoAction | null {
    // Helper to search within a list of actions
    const searchInActions = (actions: any[]): DoppeDtoAction | null => {
        for (const action of actions) {
            // Check if this is the action we're looking for
            if (action.id === actionId) {
                return action;
            }
            
            // Search in locked actions
            if (action.lockSettings?.lockActions) {
                for (const lockedAction of action.lockSettings.lockActions) {
                    if (lockedAction.id === actionId) {
                        return lockedAction;
                    }
                }
            }
        }
        return null;
    };
    
    // Search in main page actions
    const mainPageActions = bymoPageProps.actions ?? [];
    const foundInMain = searchInActions(mainPageActions);
    if (foundInMain) {
        return foundInMain;
    }
    
    // Search in inner pages actions
    const innerPages = bymoPageProps.innerPages ?? [];
    for (const innerPage of innerPages) {
        if (doppeDtoInnerPageIsContent(innerPage)) {
            const innerPageActions = innerPage.actions ?? [];
            const foundInInner = searchInActions(innerPageActions);
            if (foundInInner) {
                return foundInInner;
            }
        }
    }

    // Search in page-level lock settings (main page)
    const pageLockActions = bymoPageProps.lockSettings?.lockActions ?? [];
    for (const lockAction of pageLockActions) {
        if (lockAction.id === actionId) {
            return lockAction;
        }
    }

    // Search in page-level lock settings (all inner pages: content + vertical list pages)
    const allPagesWithLockSettings: (DoppeDtoInnerPage | undefined)[] = [
        ...innerPages,
        bymoPageProps.defaultStoreProductsPage,
        bymoPageProps.defaultBlogPostsPage,
        bymoPageProps.defaultEventsPage,
        bymoPageProps.defaultServicesPage,
        bymoPageProps.defaultCartPage,
    ];

    for (const page of allPagesWithLockSettings) {
        const pageLockSettingsActions = page?.lockSettings?.lockActions ?? [];
        for (const lockAction of pageLockSettingsActions) {
            if (lockAction.id === actionId) {
                return lockAction;
            }
        }
    }
    
    return null;
}

/**
 * Finds the lock settings for a specific page by its ID from the current page props.
 * Searches across main page, content inner pages, and default vertical pages.
 */
export function bymoPagePropsGetPageLockSettings(
    bymoPageProps: BymoPageProps,
    pageId: string
): DoppePageLockSettings | null {
    if (pageId === DoppeDtoInnerPageKnownIds.Home) {
        return bymoPageProps.lockSettings ?? null;
    }

    const innerPages = bymoPageProps.innerPages ?? [];
    for (const innerPage of innerPages) {
        if (innerPage.id === pageId) {
            return innerPage.lockSettings ?? null;
        }
    }

    const defaultPages: (DoppeDtoInnerPage | undefined)[] = [
        bymoPageProps.defaultStoreProductsPage,
        bymoPageProps.defaultBlogPostsPage,
        bymoPageProps.defaultEventsPage,
        bymoPageProps.defaultServicesPage,
        bymoPageProps.defaultCartPage,
    ];

    for (const page of defaultPages) {
        if (page && page.id === pageId) {
            return page.lockSettings ?? null;
        }
    }

    return null;
}

/**
 * Returns only page actions (main page + inner pages) WITHOUT locked actions.
 * Use this for analytics and scenarios where you need to iterate through page actions
 * and access their locked actions separately.
 */
export function bymoPagePropsResolvePageActions(bymoPageProps: PartialBymoPageProps): DoppeDtoAction[] {
    // Collect actions from main page
    const mainPageActions = bymoPageProps.actions ?? [];
    
    // Collect actions from all inner pages
    const innerPagesActions = (bymoPageProps.innerPages ?? []).flatMap(innerPage => 
        doppeDtoInnerPageIsContent(innerPage) ? (innerPage.actions ?? []) : []
    );

    // Return only page actions (NOT including locked actions)
    return [...mainPageActions, ...innerPagesActions] as DoppeDtoAction[];
}

/**
 * Returns ALL actions including page actions, their locked actions, and page-level lock actions
 * in a flattened array. Use this when you need to find any action by ID regardless of where
 * it's located, or to determine which action types need viewer components loaded.
 */
export function bymoPagePropsResolveAllActions(bymoPageProps: PartialBymoPageProps): DoppeDtoAction[] {
    const allPageActions = bymoPagePropsResolvePageActions(bymoPageProps);

    const lockedActions = evaluateFunction(() => {
        const lockedActions: DoppeDtoAction<any>[] = [];

        allPageActions.forEach(action => {
            if (action.lockSettings?.enableLock && action.lockSettings.lockActions.length > 0) {
                lockedActions.push(...action.lockSettings.lockActions);
            }
        })

        if (bymoPageProps.lockSettings?.lockActions && bymoPageProps.lockSettings.lockActions.length > 0) {
            lockedActions.push(...bymoPageProps.lockSettings.lockActions);
        }

        const allPagesWithLockSettings: (DoppeDtoInnerPage | undefined)[] = [
            ...(bymoPageProps.innerPages ?? []),
            bymoPageProps.defaultStoreProductsPage,
            bymoPageProps.defaultBlogPostsPage,
            bymoPageProps.defaultEventsPage,
            bymoPageProps.defaultServicesPage,
            bymoPageProps.defaultCartPage,
        ];

        for (const page of allPagesWithLockSettings) {
            if (page?.lockSettings?.lockActions && page.lockSettings.lockActions.length > 0) {
                lockedActions.push(...page.lockSettings.lockActions);
            }
        }

        return lockedActions;
    });

    return [...allPageActions, ...lockedActions];
}

export function isSupportHideableCoverLayout(pageLayout: BymoTemplateLayout) {
    return BymoTemplateLayout.Standard === pageLayout;
}



export function bymoTemplateLayoutSupportsDesktopBackground(pageLayout: BymoTemplateLayout) {
    return BymoTemplateLayout.Standard !== pageLayout
}

export function bymoTemplateLayoutSupportsCover(pageLayout: BymoTemplateLayout) {
    return [BymoTemplateLayout.Standard, BymoTemplateLayout.Layout1, BymoTemplateLayout.Layout3].find((layout) => {
        return pageLayout === layout;
    })
}

export function bymoTemplateLayoutSupportsHeroPhotos(pageLayout: BymoTemplateLayout) {
    return [BymoTemplateLayout.Layout2, BymoTemplateLayout.Layout4, BymoTemplateLayout.Layout5, BymoTemplateLayout.Layout6, BymoTemplateLayout.Layout8, BymoTemplateLayout.Layout9, BymoTemplateLayout.Layout10, BymoTemplateLayout.Layout11, BymoTemplateLayout.Layout12, BymoTemplateLayout.Layout14].find((layout) => {
        return pageLayout === layout;
    })
}

export function bymoTemplateLayoutSupportsHeroPhotosOverlay(pageLayout: BymoTemplateLayout) {
    return [BymoTemplateLayout.Layout6, BymoTemplateLayout.Layout9, BymoTemplateLayout.Layout12].find((layout) => {
        return pageLayout === layout;
    })
}

export function bymoPagePropsHasCoverPhoto (bymoPageProps: BymoPageProps) {
    return hideableValueIsVisibleAndNotEqualValue(bymoPageProps.coverPhoto, null)
}

export function getSocialLinkActionId(platform: DoppeDtoSocialLinkPlatform) {
    return `SOCIAL_ICON_${doppeDtoSocialLinkPlatformResolveUniqueName(platform)}`
}

export function bymoTemplateLayoutIsPremium (pageLayout: BymoTemplateLayout) {
    switch (pageLayout) {
        case BymoTemplateLayout.Layout2:
        case BymoTemplateLayout.Layout4:
        case BymoTemplateLayout.Layout5:
        case BymoTemplateLayout.Layout6:
        case BymoTemplateLayout.Layout7:
        case BymoTemplateLayout.Layout8:
        case BymoTemplateLayout.Layout9:
        case BymoTemplateLayout.Layout10:
        case BymoTemplateLayout.Layout11:
        case BymoTemplateLayout.Layout12:
        case BymoTemplateLayout.Layout13:
        case BymoTemplateLayout.Layout14: {
            return true;
        }
        default: {
            return false;
        }
    }
}

export function bymoTemplateLayoutGetDefault () {
    return BymoTemplateLayout.Blank;
}

export function bymoListLayoutGetDefault () {
    return DoppeActionLayout.Column;
}

export function bymoTemplateListLayoutIsPremium (listLayout: DoppeActionLayout) {
    switch (listLayout) {
        case DoppeActionLayout.LargeColumn:
        case DoppeActionLayout.Swiper:
        case DoppeActionLayout.Carousel:
        case DoppeActionLayout.CarouselSmall: {
            return true;
        }
        default: {
            return false;
        }
    }
}


export const HomeMenuItemId = 'a0d692e2-99eb-43ff-a7aa-bda824f9adea';

/**
 * Creates default navigation settings for a new inner pages setup
 */
export function bymoPageCreateDefaultNavigationSettings(
): BymoPageNavigationSettings {
    const menuItems: BymoPageNavigationMenuItem[] = [];

    // Add home page as first menu item
    menuItems.push({
        id: HomeMenuItemId,
        title: 'Home',
        isVisible: true,
        settings: {
            type: BymoPageNavigationMenuItemType.Page,
            pageId: DoppeDtoInnerPageKnownIds.Home
        }
    });

    return {
        // Navigation always renders as floating button in header
        // layout and position properties removed
        menuItems: menuItems,
        menuSettings: {
            searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden,
            searchPlaceholder: hideableValueCreateHidden(''),
            menuOpeningPanelType: BymoPageNavigationMenuSettingsMenuPanelDisplayType.SidePanel,
            logo: DoppeMenuLogoProfilePictureAutoValue,
            title: autoValueCreate(DoppeMenuTitleAutoSource.ProfileName),
            socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Visible,
            menuStyle: DoppeViewerNavigationMenuStyle.Classic,
            desktopMenuSettings: {
                menuStyle: DoppeViewerNavigationMenuStyle.Classic,
                menuOpeningPanelType: BymoPageNavigationMenuSettingsMenuPanelDisplayType.SidePanel,
                menuType: BymoPageNavigationMenuDesktopMenuType.Row,
                maxItemsToShow: hideableValueCreateHidden(5),
            },
            showSearchIcon: true,
            showCartIcon: true,
            showHeaderMenuInHomePage: false,
        }
    };
}

export function bymoPageMigrateNavigationSettingsFromDefaultValues(
    navigationSettings: Partial<BymoPageNavigationSettings>
): BymoPageNavigationSettings {
    const menuItems: BymoPageNavigationMenuItem[] = navigationSettings.menuItems ?? [{
        id: HomeMenuItemId,
        title: 'Home',
        isVisible: true,
        settings: {
            type: BymoPageNavigationMenuItemType.Page,
            pageId: DoppeDtoInnerPageKnownIds.Home
        }
    }];



    return {
        ...navigationSettings,
        // Navigation always renders as floating button in header
        // layout and position properties removed
        menuItems: menuItems.map(menuItem => {
            return bymoPageNavigationMenuItemMigrateFromDefaultValues(menuItem);
        }),
        menuSettings: {
            searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden,
            searchPlaceholder: hideableValueCreateHidden(''),
            menuOpeningPanelType: BymoPageNavigationMenuSettingsMenuPanelDisplayType.FullScreen,
            logo: DoppeMenuLogoProfilePictureAutoValue,
            title: autoValueCreate(DoppeMenuTitleAutoSource.ProfileName),
            socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Visible,
            menuStyle: DoppeViewerNavigationMenuStyle.Classic,
            desktopMenuSettings: {
                menuStyle: DoppeViewerNavigationMenuStyle.Classic,
                menuOpeningPanelType: BymoPageNavigationMenuSettingsMenuPanelDisplayType.SidePanel,
                menuType: BymoPageNavigationMenuDesktopMenuType.Row,
                maxItemsToShow: hideableValueCreateHidden(5),
            },
            showSearchIcon: true,
            showCartIcon: true,
            showHeaderMenuInHomePage: false,
            ...navigationSettings.menuSettings
        }
    };
}

// ============================================================================
// Inner Pages Helper Functions
// ============================================================================

/**
 * Get a specific inner page by ID from inner pages props.
 * Returns null if page is not found.
 */
export function bymoPagePropsGetInnerNavigatablePageById(
    bymoPageProps: BymoPageProps<any>,
    pageId: string
): DoppeDtoInnerPage | null {
    return bymoPageGetInnerNavigatablePages(bymoPageProps).find((page: DoppeDtoInnerPage) => page.id === pageId) || null;
}

/**
 * Get an inner page by slug from inner pages props.
 * Returns null if page is not found.
 */
export function bymoPagePropsGetInnerNavigatablePageBySlug(
    bymoPageProps: BymoPageProps<any>,
    slug: string
): DoppeDtoInnerPage | null {
    return bymoPageGetInnerNavigatablePages(bymoPageProps).find((page: DoppeDtoInnerPage) => page.slug === slug) || null;
}

// ============================================================================
// Page-Aware Content Resolution
// ============================================================================

/**
 * Resolves which actions to display based on the current page.
 * For inner pages: returns actions from the specific inner page
 * For main page (DoppeDtoInnerPageKnownIds.Home): returns all actions from the main page
 */
export function bymoPageResolveActionsFromPage(
    bymoPageProps: BymoPageProps,
    currentPageId: string
): DoppeDtoAction[] {
    if (currentPageId === DoppeDtoInnerPageKnownIds.Home) {
        // Main page - return main page actions
        return bymoPageProps.actions;
    }
    
    // Find the specific inner page
    const currentInnerPage = bymoPagePropsGetInnerNavigatablePageById(bymoPageProps, currentPageId);
    if (currentInnerPage && doppeDtoInnerPageIsContent(currentInnerPage)) {
        return currentInnerPage.actions;
    }
    
    return [];
}

// ============================================================================
// URL-Based Page Resolution
// ============================================================================

/**
 * Extracts a page slug from the current URL.
 * Looks for patterns like:
 * - /page/about
 * - /page/product/abc123 (returns 'product')
 * - /p/contact
 * - ?page=about
 *
 * Returns null if no page slug is found.
 * Returns only the first segment - use bymoPageExtractPathSegmentsFromUrl for additional segments.
 */
export function bymoPageExtractSlugFromUrl(url: string = typeof window !== 'undefined' ? window.location.href : ''): string | null {
    try {
        const urlObj = new URL(url);

        // Check URL path patterns: /page/slug or /p/slug (with optional additional segments)
        const pathMatch = urlObj.pathname.match(/^\/(?:page|p)\/([^\/]+)(?:\/.*)?$/);
        if (pathMatch) {
            return decodeURIComponent(pathMatch[1]);
        }

        // Check query parameter: ?page=slug
        const pageParam = urlObj.searchParams.get('page');
        if (pageParam) {
            return decodeURIComponent(pageParam);
        }

        // Check hash fragment: #page/slug
        const hashMatch = urlObj.hash.match(/^#page\/([^\/]+)(?:\/.*)?$/);
        if (hashMatch) {
            return decodeURIComponent(hashMatch[1]);
        }

        return null;
    } catch (error) {
        console.warn('Failed to parse URL for page slug extraction:', error);
        return null;
    }
}

/**
 * Resolves a page slug to an inner page ID using inner pages props.
 * Returns null if the slug is not found.
 */
export function bymoPageResolveSlugToInnerPageId(
    bymoPageProps: BymoPageProps,
    slug: string
): string | null {
    const innerPage = bymoPagePropsGetInnerNavigatablePageBySlug(bymoPageProps, slug);
    return innerPage ? innerPage.id : null;
}

/**
 * Resolves the current page ID from URL slug, with fallback to main page.
 * This is the main function to use for URL-based page resolution.
 */
export function bymoPageResolveCurrentPageIdFromUrl(
    bymoPageProps: BymoPageProps,
    url?: string
): string | null {
    // Extract slug from URL
    const slug = bymoPageExtractSlugFromUrl(url);

    if (!slug) {
        // No slug in URL - return null to use default behavior (main page)
        return null;
    }

    // Try to resolve slug to inner page ID
    const innerPageId = bymoPageResolveSlugToInnerPageId(bymoPageProps, slug);

    if (innerPageId) {
        return innerPageId;
    }

    // Slug not found - return null to fallback to main page
    console.warn(`Inner page slug "${slug}" not found, falling back to main page`);
    return null;
}

// ============================================================================
// Menu Title Resolution
// ============================================================================

/**
 * Resolves the menu title to display based on page settings and context.
 * 
 * The menu title can be:
 * - Hidden: returns empty string
 * - A string: returns the custom title
 * - An AutoValue: uses the resolved profile name value
 * 
 * Used by both the viewer component (DoppePageMenuTitleView) and the settings panel.
 */
export function bymoPagePropsResolveMenuTitle(
    bymoPageProps: BymoPageProps,
    options: {
        userDisplayName: string;
        userAccountName: string;
        defaultProfileNameAutoSource: DoppeProfileNameAutoSource;
    }
): string {
    const {
        userDisplayName,
        userAccountName,
        defaultProfileNameAutoSource
    } = options;

    // Check if menu title is visible
    if (hideableValueIsVisible(bymoPageProps.navigationSettings.menuSettings.title)) {
        // First resolve the profile name (which the menu title may use as its auto value)
        const resolvedProfileName = bymoPagePropsResolveProfileName(bymoPageProps, {
            userDisplayName: userDisplayName,
            userAccountName: userAccountName,
            defaultProfileNameAutoSource: defaultProfileNameAutoSource
        });

        const titleSource: DoppeMenuTitleSource = bymoPageProps.navigationSettings.menuSettings.title;
        return doppeMenuTitleSourceResolve(titleSource, resolvedProfileName);
    }

    return '';
}

/**
 * Resolves the menu logo to display based on navigation settings.
 * The logo can be:
 * - An AutoValue<'profilePicture'>: uses the resolved profile picture from Link in Bio
 * - A WixMediaResource: uses the custom media directly
 *
 * Returns null if the logo is hidden.
 */
export function bymoPagePropsResolveMenuLogo(
    bymoPageProps: BymoPageProps,
    options: {
        accountProfilePicture: DoppeDtoUser['profilePicture'];
    }
): WixMediaResource | null {
    const { accountProfilePicture } = options;

    // Check if logo is hidden
    if (hideableValueIsHidden(bymoPageProps.navigationSettings.menuSettings.logo)) {
        return null;
    }

    // First, resolve the profile picture from Link in Bio settings
    const profilePictureLinkInBioSource = hideableValueGetVisibleValue(
        bymoPageProps.profilePictureLinkInBio,
        null
    );

    const resolvedProfilePicture = profilePictureLinkInBioSource !== null
        ? doppePageMediaAutoSourceResolve(profilePictureLinkInBioSource, {
            accountProfilePicture: accountProfilePicture
        })
        : null;

    // Get the actual logo source value (not the HideableValue)
    const logoSource = hideableValueGetValue(bymoPageProps.navigationSettings.menuSettings.logo);
    return doppeMenuLogoSourceResolve(logoSource, resolvedProfilePicture);
}

/**
 * Resolves the profile name to display based on page settings and context.
 */
export function bymoPagePropsResolveProfileName(
    bymoPageProps: BymoPageProps,
    options: {
        userDisplayName: string;
        userAccountName: string;
        defaultProfileNameAutoSource: DoppeProfileNameAutoSource;
    }
): string {
    const {
        userDisplayName,
        userAccountName,
        defaultProfileNameAutoSource
    } = options;

    const profileName = bymoPageProps.profileName;

    // If profile name is hidden, return empty string
    if (!hideableValueIsVisible(profileName)) {
        return '';
    }

    // If it's a string, return it directly
    if (typeof profileName === 'string') {
        return profileName;
    }

    // If it's an AutoValue, resolve based on the auto source
    const profileNameAutoSource = profileName === null ? defaultProfileNameAutoSource : autoValueGetValue(profileName);

    switch (profileNameAutoSource) {
        case DoppeProfileNameAutoSource.DisplayName: {
            return userDisplayName;
        }
        case DoppeProfileNameAutoSource.AccountName: {
            return `@${userAccountName}`;
        }
    }
}